"use client";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { truncateDecimals } from "../utils/numberFormatter";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { Skeleton } from "./ui/skeleton";
import QuoteDetails from "./BuyQuoteWallet";
import { getQuote } from "../services/digitalWalletService";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { useAuth } from "../context/AuthContext";
import AssetSelectorWallet from "./AssetSelectorWallet";
import StatusStep from "./ui/status-step";
import { useQueryClient } from "@tanstack/react-query";
import { useAvailableBalances } from "../hooks/query/queries";
import { useFeatureAccess } from "./FeatureGate";
import { refreshBalanceQueries } from "../services/invalidator";
import CardSpinningLoader from "./CardSpinningLoader";

interface Asset {
  assetId: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  [key: string]: unknown;
}

interface TradingPair {
  id: string;
  pair: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  type?: string;
  rate?: number;
  postTradeEnabled?: boolean;
  availableAmount?: number;
  formattedAvailableAmount?: string;
  [key: string]: unknown;
}

interface Quote {
  incomingAmount?: number;
  outgoingAmount?: number;
  serviceFee?: number;
  feePercentage?: {
    $numberDecimal?: string;
  };
  tradeType?: string;
  pair?: string;
  userId?: string;
  rate?: number;
  assetId?: string;
  quoteId?: string;
  createdAt?: number;
  expiresAt?: number;
  [key: string]: unknown;
}

interface BuySellPageProps {
  onDashboard?: boolean;
  assetToSelect?: unknown;
  tab?: string;
  quickActionTab?: string;
  childTab?: string;
  tradingAssets?: unknown;
  pairObject?: Record<string, unknown>;
  zarBalance: number;
}

const tradeTypes = ["Buy", "Sell"] as const;
type TradeType = (typeof tradeTypes)[number];
export default function DashboardBuySell({
  onDashboard = false,
  assetToSelect,
  tab,
  quickActionTab,
  childTab,
  tradingAssets,
  pairObject,
  zarBalance,
}: BuySellPageProps) {
  const [tradeType, setTradeType] = useState<TradeType | null>(null);
  const [amount, setAmount] = useState<string | number | null>(null);
  const [inputAmount, setinputAmount] = useState<number | null>(null);
  const [orderAmount, setOrderAmount] = useState<number | null>(null);
  const [incomingAmount, setIncomningAmount] = useState<number | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedSaleAsset, setSelectedSaleAsset] = useState<Asset | null>(
    null
  );
  const [selectedPair, setSelectedPair] = useState<TradingPair | null>(null);
  const [selectedSalePair, setSelectedSalePair] = useState<TradingPair | null>(
    null
  );
  const [tradeRate, setTradeRate] = useState<{ rate: number } | null>(null);
  const [quote, setQuote] = useState<Quote>({});
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showQuote, setShowQuote] = useState<boolean>(false);
  const [order, setOrder] = useState<boolean>(false);
  const [isTrading, setIsTrading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [tradeResult, setTradeResult] = useState<{
    success: boolean;
    message?: string;
    trade?: unknown;
  } | null>(null);
  const [isPostTrade, setIsPostTrade] = useState<boolean>(false);
  const [currentPairs, setCurrentPairs] = useState<unknown>(null);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [isPairPostTradeAllowed, setIsPairPostTradeAllowed] =
    useState<boolean>(false);
  const [isOrderCreationView, setIsOrderCreationView] =
    useState<boolean>(false);
  const [fetchingQuoteAfterOrder, setFetchingQuoteAfterOrder] =
    useState<boolean>(false);
  const isConfirmingRef = useRef<boolean>(false);
  const queryClient = useQueryClient();
  const { userProfile, settings } = useAuth();
  const { toast } = useToast();
  const isPostTradeAllowed = settings?.postTradeSettlements || false;
  const { flags: featureFlags, isLoading: isLoadingFlags } = useFeatureAccess(
    "mainFeatures",
    ["buyCrypto", "sellCrypto"]
  );

  const isBuyEnabled = (featureFlags as { buyCrypto?: boolean })?.buyCrypto;
  const isSellEnabled = (featureFlags as { sellCrypto?: boolean })?.sellCrypto;

  const { data: walletAssets } = useAvailableBalances(userProfile?._id);

  useEffect(() => {
    if (isLoadingFlags) return;
    if (tradeType !== null) return;
    if (isBuyEnabled) {
      setTradeType("Buy");
    } else if (isSellEnabled) {
      setTradeType("Sell");
    }
  }, [isBuyEnabled, isSellEnabled, tradeType, isLoadingFlags]);

  const fiatBalances = useMemo(
    () =>
      walletAssets?.map((asset: Asset) => {
        return {
          asset: asset.asset,
          valueInLocalCurrency:
            asset.asset === "zar"
              ? asset.availableAmountFormatted
              : asset.valueInLocalCurrency,
        };
      }) ?? [],
    [walletAssets]
  );

  const getSuccessConfig = () => {
    const trade = tradeResult?.trade as
      | {
          type?: string;
          incomingAmount?: unknown;
          outgoingAmount?: unknown;
          incomingAsset?: string;
          outgoingAsset?: string;
          price?: unknown;
        }
      | null
      | undefined;
    const parseDecimal = (value: unknown): number => {
      if (typeof value === "number") return value;
      if (typeof value === "string") return parseFloat(value);
      const v = value as { $numberDecimal?: string } | null | undefined;
      if (v?.$numberDecimal) return parseFloat(v.$numberDecimal);
      return 0;
    };

    const currentTradeType =
      (trade?.type === "buy"
        ? "Buy"
        : trade?.type === "sell"
          ? "Sell"
          : null) ||
      tradeType ||
      "Buy";

    if (currentTradeType === "Buy") {
      const assetAmount = trade
        ? parseDecimal(trade.incomingAmount)
        : incomingAmount || 0;
      const assetSymbol =
        trade?.incomingAsset?.toUpperCase() ||
        selectedAsset?.asset?.toUpperCase() ||
        "CRYPTO";
      const targetPrice =
        orderAmount ||
        (trade ? parseDecimal(trade.price) : tradeRate?.rate || 0);

      if (order) {
        const config = {
          title: "Your buy order has been set",
          description: `You'll buy ${truncateDecimals(assetAmount, 6)} ${assetSymbol} if its price goes down to R ${truncateDecimals(targetPrice, 2)}`,
          buttonText: "Done",
        };
        return config;
      } else {
        const config = {
          title: "Your portfolio just got stronger",
          description: `Your crypto is now in your wallet and ready to go.`,
          buttonText: "Done",
        };
        return config;
      }
    }

    if (currentTradeType === "Sell") {
      const assetAmount = trade
        ? parseDecimal(trade.outgoingAmount)
        : inputAmount || 0;
      const assetSymbol =
        trade?.outgoingAsset?.toUpperCase() ||
        selectedSaleAsset?.asset?.toUpperCase() ||
        "CRYPTO";
      const targetPrice =
        orderAmount ||
        (trade ? parseDecimal(trade.price) : tradeRate?.rate || 0);

      if (order) {
        const config = {
          title: "Your sell order has been set",
          description: `You'll sell ${truncateDecimals(assetAmount, 6)} ${assetSymbol} if its price goes up to R ${truncateDecimals(targetPrice, 2)}`,
          buttonText: "Done",
        };
        return config;
      }

      const config = {
        title: "Cash in Hand.",
        description: (
          <>
            You've freed up funds and kept <br /> control of your next move.
          </>
        ),
        buttonText: "Done",
      };
      return config;
    }

    const config = {
      title: "Transaction Successful",
      description: "Your transaction has been completed.",
      buttonText: "Done",
    };
    return config;
  };

  const getFailureConfig = () => {
    const trade = tradeResult?.trade as { type?: string } | null | undefined;
    const currentTradeType =
      tradeType ||
      (trade?.type === "buy"
        ? "Buy"
        : trade?.type === "sell"
          ? "Sell"
          : null) ||
      "Buy";

    if (currentTradeType === "Sell") {
      const config = {
        title: "Transaction failed",
        description:
          "Your crypto sale was not successful. Please try again. If the problem persists, please contact support.",
        buttonText: "Try Again",
      };
      return config;
    }

    const config = {
      title: "Transaction failed",
      description:
        "Your crypto purchase was not successful. Please try again. If the problem persists, please contact support.",
      buttonText: "Try Again",
    };
    return config;
  };

  useEffect(() => {
    const assetId =
      tradeType === "Buy" ? selectedAsset?.assetId : selectedSaleAsset?.assetId;
    if (!assetId || !pairObject) return;

    let currentAssetPairs: unknown = undefined;
    if (Object.prototype.hasOwnProperty.call(pairObject, assetId)) {
      currentAssetPairs = pairObject[assetId as keyof typeof pairObject];
    }

    setCurrentPairs(currentAssetPairs);
  }, [selectedAsset, selectedSaleAsset, pairObject, tradeType]);

  useEffect(() => {
    if (!isBuyEnabled && isSellEnabled) {
      setTradeType("Sell");
    } else if (isBuyEnabled && !isSellEnabled) {
      setTradeType("Buy");
    }
  }, [isBuyEnabled, isSellEnabled]);

  useEffect(() => {
    const validTabs: TradeType[] = ["Buy", "Sell"];
    if (!tab) return;
    if (validTabs.includes(tab as TradeType)) {
      if (
        (tab === "Buy" && isBuyEnabled) ||
        (tab === "Sell" && isSellEnabled)
      ) {
        setTradeType(tab as TradeType);
      }
    }
  }, [tab, isBuyEnabled, isSellEnabled]);

  useEffect(() => {
    const validTabs: TradeType[] = ["Buy", "Sell"];
    if (!childTab) return;
    if (validTabs.includes(childTab as TradeType)) {
      if (
        (childTab === "Buy" && isBuyEnabled) ||
        (childTab === "Sell" && isSellEnabled)
      ) {
        setTradeType(childTab as TradeType);
      }
    }
  }, [childTab, isBuyEnabled, isSellEnabled]);

  useEffect(() => {
    if (assetToSelect) {
      const assetToSelectId =
        (assetToSelect as Asset).assetId || (assetToSelect as Asset).asset;
      const currentAssetId = selectedAsset?.assetId ?? selectedAsset?.asset;

      if (assetToSelectId !== currentAssetId) {
        setSelectedAsset(assetToSelect as Asset);
      }
    }
  }, [assetToSelect, selectedAsset]);

  useEffect(() => {
    const validTabs: TradeType[] = ["Buy", "Sell"];
    if (!quickActionTab) return;
    if (validTabs.includes(quickActionTab as TradeType)) {
      if (
        (quickActionTab === "Buy" && isBuyEnabled) ||
        (quickActionTab === "Sell" && isSellEnabled)
      ) {
        setTradeType(quickActionTab as TradeType);
      }
    }
  }, [quickActionTab, isBuyEnabled, isSellEnabled]);

  const handleConfirm = async (result: unknown): Promise<void> => {
    if (isConfirmingRef.current) {
      return;
    }

    isConfirmingRef.current = true;
    setLoading(true);
    setShowQuote(false);

    const resultData = result as {
      success?: boolean;
      data?: { data?: { trade?: unknown }; trade?: unknown };
      error?: string;
      message?: string;
      trade?: unknown;
    };

    const tradeData =
      resultData.trade ||
      (resultData.data as { trade?: unknown })?.trade ||
      (resultData.data as { data?: { trade?: unknown } })?.data?.trade;
    const responseData = resultData.data;

    const isSuccess =
      resultData.success === true ||
      (resultData.success !== false &&
        resultData.success !== undefined &&
        tradeData !== undefined &&
        tradeData !== null);

    const actualTradeData =
      tradeData ||
      (responseData as { data?: { trade?: unknown } })?.data?.trade ||
      (responseData as { trade?: unknown })?.trade;

    const tradeResultData = {
      success: isSuccess,
      message: isSuccess
        ? (resultData.message as string) ||
          (responseData as { message?: string })?.message ||
          "Trade completed successfully"
        : (resultData.message as string) ||
          (resultData.error as string) ||
          (responseData as { message?: string })?.message ||
          "Trade failed. Please try again.",
      trade: actualTradeData ?? null,
    };

    setTradeResult(tradeResultData);
    setShowSuccess(true);
    setQuote({});

    setLoading(false);
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        isConfirmingRef.current = false;
        resolve();
      }, 100);
    });
  };

  const handleDone = async (): Promise<void> => {
    try {
      await refreshBalanceQueries(queryClient, 0);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["cryptoAssets"],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ["userOrders"],
          exact: false,
        }),
      ]);
    } catch (err) {
      // continue to form even if refresh fails
    }

    setSelectedSaleAsset(null);
    setSelectedAsset(null);
    setSelectedPair(null);
    setSelectedSalePair(null);
    setShowSuccess(false);
    setTradeResult(null);
    setShowQuote(false);
    setQuote({});
    setAmount(null);
    setinputAmount(null);
    setOrderAmount(null);
    setIncomningAmount(null);
    setOrder(false);
    setCurrentPairs(null);
    setDisabled(false);
    setIsPairPostTradeAllowed(false);
    setIsTrading(false);
    setIsOrderCreationView(false);
    setFetchingQuoteAfterOrder(false);
  };

  const handleBack = (): void => {
    setShowQuote(false);
  };

  const handleAssetChange = useCallback((asset: Asset): void => {
    setSelectedAsset(asset);
  }, []);

  const handleAssetSaleChange = useCallback((asset: Asset): void => {
    setSelectedSaleAsset(asset);
  }, []);

  const handlePairChange = useCallback((pair: TradingPair): void => {
    setSelectedPair(pair);
  }, []);

  const handlePairsSaleChange = useCallback((pair: TradingPair): void => {
    setSelectedSalePair(pair);
  }, []);

  const handleInputAmount = useCallback(
    (outAmount: number, inAmount: number): void => {
      setinputAmount(outAmount);
      setIncomningAmount(inAmount);
    },
    []
  );

  const handleInputSaleAmount = useCallback(
    (inAmount: number, outAmount: number): void => {
      setinputAmount(outAmount);
      setIncomningAmount(inAmount);
    },
    []
  );

  const handleOrderBuy = useCallback((targetAmount: number): void => {
    setOrderAmount(targetAmount);
    setOrder(true);
  }, []);

  const handleOrderCancel = useCallback((): void => {
    setOrder(false);
  }, []);

  const handleOrderViewChange = useCallback((isOrderView: boolean): void => {
    setIsOrderCreationView(isOrderView);
  }, []);

  const orderBuyData = {
    userId: userProfile?._id,
    currencyPair: selectedPair?.pair,
    incomingAsset: selectedAsset?.asset,
    outgoingAsset: selectedPair?.asset,
    orderType: "below",
    type: "buy",
    price: tradeRate?.rate,
    targetPrice: orderAmount,
    incomingAmount: incomingAmount,
    outgoingAmount: inputAmount,
    serviceFee: 0,
    brokerPercentage: 0,
    note: "",
    isPostTrade: isPostTrade,
    assetId: selectedAsset?.assetId,
  };
  const orderSellData = {
    userId: userProfile?._id,
    currencyPair: selectedSalePair?.pair,
    incomingAsset: selectedSalePair?.asset,
    outgoingAsset: selectedSaleAsset?.asset,
    orderType: "above",
    type: "sell",
    price: tradeRate?.rate,
    targetPrice: orderAmount,
    incomingAmount: inputAmount,
    outgoingAmount: incomingAmount,
    serviceFee: 0,
    brokerPercentage: 0,
    note: "",
    isPostTrade: isPostTrade,
    assetId: selectedSaleAsset?.assetId,
  };
  const buyData = {
    userId: userProfile?._id,
    tradeType: tradeType?.toLowerCase() ?? "buy",
    pair: selectedPair?.pair,
    incomingAmount: incomingAmount,
    outgoingAmount: inputAmount,
    price: quote?.rate ?? tradeRate?.rate,
    serviceFee: 0,
    feePercentage: 0,
    incomingAsset: selectedAsset?.asset,
    outgoingAsset: selectedPair?.asset,
    brokerPercentage: 0,
    brokerAmount: 0,
    isPostTrade: isPostTrade,
  };

  const sellData = {
    userId: userProfile?._id,
    tradeType: tradeType?.toLowerCase() ?? "sell",
    pair: selectedSalePair?.pair,
    incomingAmount: inputAmount,
    outgoingAmount: incomingAmount,
    price: quote?.rate ?? tradeRate?.rate,
    serviceFee: 0,
    feePercentage: 0,
    incomingAsset: selectedSalePair?.asset,
    outgoingAsset: selectedSaleAsset?.asset,
    brokerPercentage: 0,
    brokerAmount: 0,
    isPostTrade: isPostTrade,
  };

  const quoteData = {
    incomingAsset: selectedAsset?.asset,
    outgoingAsset: selectedPair?.asset,
    assetId: selectedAsset?.assetId,
    amount: inputAmount,
    isPostTrade: isPostTrade,
    tradeType: tradeType?.toLowerCase() ?? "buy",
    pair: selectedPair?.pair,
    isCustom: order,
    userId: userProfile?._id,
    price: orderAmount ? parseFloat(String(orderAmount)) : 0,
  };

  const quoteSaleData = {
    incomingAsset: selectedSalePair?.asset,
    outgoingAsset: selectedSaleAsset?.asset,
    assetId: selectedSaleAsset?.assetId,
    amount: incomingAmount,
    isPostTrade: isPostTrade,
    tradeType: tradeType?.toLowerCase() ?? "sell",
    pair: selectedSalePair?.pair,
    isCustom: order,
    userId: userProfile?._id,
    price: orderAmount ? parseFloat(String(orderAmount)) : 0,
  };

  const handleTabChange = (value: string): void => {
    setAmount(null);
    setinputAmount(null);
    setOrderAmount(null);
    setIncomningAmount(null);
    setSelectedAsset(null);
    setSelectedSaleAsset(null);
    setSelectedPair(null);
    setSelectedSalePair(null);
    setTradeRate(null);
    setQuote({});
    setOrder(false);
    setIsPostTrade(false);
    setDisabled(false);
    setCurrentPairs(null);
    setIsPairPostTradeAllowed(false);
    setIsTrading(false);
    setLoading(false);
    setTradeResult(null);
    setShowSuccess(false);
    setShowQuote(false);
    setTradeType(value as TradeType);
  };

  const trade = async (): Promise<void> => {
    setIsTrading(true);
    try {
      if (tradeType === "Buy") {
        const numericAmount = parseFloat(String(inputAmount ?? 0));
        if (
          !inputAmount ||
          inputAmount === null ||
          isNaN(numericAmount) ||
          numericAmount <= 0
        ) {
          toast({
            description: "Please enter a valid amount",
            variant: "error",
          });
          setIsTrading(false);
          return;
        }

        try {
          const result = await getQuote(quoteData);
          const resultData = result.data.data;
          setQuote(resultData);
          setShowQuote(true);
        } catch (error) {
          toast({
            description: `${error}`,
            variant: "error",
          });
        }
      }
      if (tradeType === "Sell") {
        const numericAmount = parseFloat(String(incomingAmount ?? 0));
        if (
          !incomingAmount ||
          incomingAmount === null ||
          isNaN(numericAmount) ||
          numericAmount <= 0
        ) {
          toast({
            description: "Please enter a valid amount",
            variant: "error",
          });
          setIsTrading(false);
          return;
        }

        try {
          const result = await getQuote(quoteSaleData);
          const resultData = result.data.data;
          setQuote(resultData);
          setShowQuote(true);
        } catch (error) {
          toast({
            description: `${error}`,
            variant: "error",
          });
        }
      }
    } catch (error) {
    } finally {
      setIsTrading(false);
    }
  };

  useEffect(() => {
    if (!selectedPair && !selectedSalePair) return;

    const isAllowed =
      selectedPair?.postTradeEnabled ??
      selectedSalePair?.postTradeEnabled ??
      false;

    setIsPairPostTradeAllowed(isAllowed);
    if (!isAllowed) {
      setIsPostTrade(false);
    }
  }, [selectedPair, selectedSalePair]);
  return (
    <>
      {fetchingQuoteAfterOrder && (
        <Card
          className={`flex flex-col w-full pt-4 bg-white ${onDashboard ? "!shadow-none border-none" : ""}`}
        >
          <CardContent className="flex flex-col items-center justify-center px-6 h-[600px]">
            <CardSpinningLoader />
          </CardContent>
        </Card>
      )}
      {!showQuote && !showSuccess && !fetchingQuoteAfterOrder && (
        <Card
          className={`flex flex-col w-full pt-4 bg-white ${onDashboard ? "!shadow-none border-none" : ""}`}
        >
          {!isOrderCreationView && (isBuyEnabled || isSellEnabled) && (
            <div className="px-4">
              <Tabs onValueChange={handleTabChange} className="mb-8">
                <TabsList
                  className={`grid rounded-full h-[64px] bg-transparent border border-[#EBE9EF] border-[1px] px-[8px] ${isBuyEnabled && isSellEnabled ? "grid-cols-2" : "grid-cols-1"}`}
                >
                  {isBuyEnabled && (
                    <TabsTrigger
                      value="Buy"
                      className={`py-3 text-[14px] font-semibold rounded-full transition-colors ${
                        tradeType === "Buy"
                          ? "bg-purple-900 text-white"
                          : "text-gray-600"
                      }`}
                    >
                      Buy
                    </TabsTrigger>
                  )}
                  {isSellEnabled && (
                    <TabsTrigger
                      value="Sell"
                      className={`py-3 text-[14px] font-semibold rounded-full transition-colors ${
                        tradeType === "Sell"
                          ? "bg-purple-900 text-white"
                          : "text-gray-600"
                      }`}
                    >
                      Sell
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>
          )}
          <CardContent className="px-2">
            <div className="space-y-2 px-3">
              {!showQuote && (
                <AssetSelectorWallet
                  zarBalance={zarBalance}
                  hotActionAsset={
                    assetToSelect && (tab === tradeType || !tab)
                      ? (assetToSelect as Asset | undefined)
                      : null
                  }
                  onTarget={handleOrderBuy}
                  orderAmount={orderAmount}
                  onTargetCancel={handleOrderCancel}
                  onSelect={
                    tradeType === "Buy"
                      ? handleAssetChange
                      : handleAssetSaleChange
                  }
                  fiatBalances={fiatBalances}
                  setDisabled={setDisabled}
                  setSelectedAsset={setSelectedAsset}
                  selectedAsset={selectedAsset}
                  selectedSaleAsset={selectedSaleAsset}
                  onChange={
                    tradeType === "Buy"
                      ? handleInputAmount
                      : handleInputSaleAmount
                  }
                  setAmount={setAmount}
                  amount={amount}
                  tradingAssets={tradingAssets as Asset[] | undefined}
                  pair={tradeType === "Buy" ? selectedPair : selectedSalePair}
                  setParentRate={setTradeRate}
                  typeOfTrade={tradeType ?? "Buy"}
                  isPostTrade={isPostTrade}
                  currentPairs={
                    currentPairs as TradingPair[] | null | undefined
                  }
                  onPairSelect={
                    tradeType === "Buy"
                      ? handlePairChange
                      : handlePairsSaleChange
                  }
                  onOrderViewChange={handleOrderViewChange}
                  onDashboard={onDashboard}
                  onOrderCreatedAndDone={(amount) => {
                    setFetchingQuoteAfterOrder(true);
                    setOrderAmount(amount);
                    setOrder(true);
                    requestAnimationFrame(() => {
                      void trade().finally(() =>
                        setFetchingQuoteAfterOrder(false)
                      );
                    });
                  }}
                />
              )}
            </div>

            {!isOrderCreationView && (
              <>
                {isPostTradeAllowed && !order && isPairPostTradeAllowed && (
                  <label className="flex items-center space-x-2 cursor-pointer my-3">
                    <input
                      type="checkbox"
                      checked={isPostTrade}
                      onChange={() => setIsPostTrade(!isPostTrade)}
                      className="w-5 h-5 accent-rose-600 cursor-pointer border border-gray-100"
                    />
                    <span className="text-gray-700">
                      {tradeType === "Sell" ? "Sell" : "Buy"} using post-trade
                      settlement
                    </span>
                  </label>
                )}
                <div className="w-full flex justify-center">
                  <Button
                    onClick={() => void trade()}
                    disabled={isTrading || disabled}
                    className={`py-3.5 h-auto mx-auto rounded-full text-sm font-medium ${disabled ? "bg-[#F2F1F4] text-[#9D96AF] cursor-not-allowed" : "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white"} ${onDashboard ? "w-full mx-3 mt-8" : "max-w-[300px] min-w-[300px]"}`}
                  >
                    {isTrading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading...
                      </div>
                    ) : (
                      "Continue"
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
      {showQuote && !showSuccess && (
        <QuoteDetails
          onBack={handleBack}
          data={quote}
          tradeData={buyData}
          orderData={orderBuyData}
          assetId={selectedAsset?.assetId}
          saleAssetId={selectedSaleAsset?.assetId}
          rate={tradeRate?.rate}
          orderSaleData={orderSellData}
          tradeSellData={sellData}
          typeOfTrade={tradeType ?? "Buy"}
          isOrder={order}
          onConfirm={handleConfirm}
          isPostTrade={isPostTrade}
          userId={userProfile?._id}
          onRefresh={trade}
          assetIcon={
            tradeType === "Buy"
              ? selectedAsset?.assetIcon
              : selectedSaleAsset?.assetIcon
          }
          assetName={
            tradeType === "Buy"
              ? selectedAsset?.assetName
              : selectedSaleAsset?.assetName
          }
          assetSymbol={
            tradeType === "Buy"
              ? selectedAsset?.asset
              : selectedSaleAsset?.asset
          }
          dashboard={onDashboard}
        />
      )}
      {loading ? (
        <Skeleton className="w-full max-w-5xl bg-gray-600 rounded-2xl shadow-xl p-12 flex flex-col items-center text-center" />
      ) : showSuccess && !showQuote && tradeResult ? (
        <Card
          className={`w-full shadow-xl border-gray-100 rounded-3xl overflow-hidden ${onDashboard ? "!shadow-none border-none" : ""}`}
        >
          <CardContent className="px-6 py-32">
            <StatusStep
              isSuccess={tradeResult.success}
              onContinue={handleDone}
              successConfig={getSuccessConfig()}
              failureConfig={getFailureConfig()}
              hideCard={true}
            />
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}
